/**
 * Detect video provider from a URL and return an iframe-ready embed URL when applicable.
 *
 * Returns: { kind: "youtube" | "vimeo" | "file", embedUrl: string, originalUrl: string }
 *  - "file" = direct video file (MP4/WebM/etc), play with HTML <video>
 *  - "youtube"/"vimeo" = play with <iframe>
 */
export function detectVideoSource(url) {
    if (!url) return { kind: "file", embedUrl: "", originalUrl: "" };

    // YouTube
    // Handles: https://www.youtube.com/watch?v=ID&t=120s
    //          https://youtu.be/ID
    //          https://youtu.be/ID?t=120
    //          https://www.youtube.com/embed/ID
    //          https://www.youtube.com/shorts/ID
    const ytPatterns = [
        /(?:youtube\.com\/watch\?(?:.*&)?v=)([A-Za-z0-9_-]{6,})/,
        /(?:youtu\.be\/)([A-Za-z0-9_-]{6,})/,
        /(?:youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/,
        /(?:youtube\.com\/shorts\/)([A-Za-z0-9_-]{6,})/,
    ];
    for (const re of ytPatterns) {
        const m = url.match(re);
        if (m) {
            const id = m[1];
            // Capture start time
            const tMatch = url.match(/[?&]t=(\d+)(?:s)?|[?&]start=(\d+)/);
            const start = tMatch ? parseInt(tMatch[1] || tMatch[2], 10) : null;
            const params = new URLSearchParams({
                rel: "0",
                modestbranding: "1",
                playsinline: "1",
            });
            if (start) params.set("start", String(start));
            return {
                kind: "youtube",
                embedUrl: `https://www.youtube.com/embed/${id}?${params.toString()}`,
                originalUrl: url,
            };
        }
    }

    // Vimeo
    // Handles:
    //   https://vimeo.com/12345678                — public
    //   https://vimeo.com/12345678/abcd1234       — unlisted (hash REQUIRED to bypass "Faça login no Vimeo")
    //   https://player.vimeo.com/video/12345678?h=abcd1234
    //   https://player.vimeo.com/video/12345678/abcd1234
    const vimeoPlayerWithHash = url.match(/player\.vimeo\.com\/video\/(\d+)(?:\/([a-zA-Z0-9]+))?(?:[?&]h=([a-zA-Z0-9]+))?/);
    const vimeoSiteWithHash = url.match(/vimeo\.com\/(?!video\/)(\d+)(?:\/([a-zA-Z0-9]+))?/);
    const vimeoVideoPath = url.match(/vimeo\.com\/video\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
    const vimeoAny = vimeoPlayerWithHash || vimeoVideoPath || vimeoSiteWithHash;
    if (vimeoAny) {
        const id = vimeoAny[1];
        const hash = vimeoAny[2] || vimeoAny[3] || null;
        const params = new URLSearchParams({ title: "0", byline: "0", portrait: "0" });
        if (hash) params.set("h", hash);
        return {
            kind: "vimeo",
            embedUrl: `https://player.vimeo.com/video/${id}?${params.toString()}`,
            originalUrl: url,
        };
    }

    // Default: assume direct file
    return { kind: "file", embedUrl: url, originalUrl: url };
}
